/**
 * Utility functions for working with event accommodations
 */

import moment from "moment";

/** The seven room types stored on `events.accommodations`. */
export const ROOM_TYPES = ["Single", "Double", "Twin", "ROH", "Suite", "Staff", "VIP"] as const;
export type RoomType = (typeof ROOM_TYPES)[number];

/** Row label for a night added before the program's first night. */
export const PRE_NIGHT_LABEL = "Pre-night";

/** Row label for a night added after the program's last night. */
export const POST_NIGHT_LABEL = "Post-night";

/** Row-label prefix for program (peak) nights, numbered from 1: "Night 1", "Night 2", … */
export const PEAK_NIGHT_LABEL_PREFIX = "Night";

/** Per-night counts indexed by room type. All seven keys present. */
export type RoomCounts = Record<RoomType, number>;

/** Canonical in-memory accommodations shape: night (YYYY-MM-DD) → counts. */
export type RoomsByNight = Record<string, RoomCounts>;

/** Save shape on `nowadays.events.accommodations`: array of nightly entries. */
export type AccommodationsRow = Array<{ date: string } & RoomCounts>;

/**
 * Fresh `RoomCounts` with every type at zero. Used when lazily creating a
 * night entry on first cell edit, and as the seed when serializing canonical
 * data so every persisted entry has all seven keys present.
 *
 * @returns A new zero-filled `RoomCounts` (always a fresh object — safe to spread / mutate).
 */
export function createZeroCounts(): RoomCounts {
  return { Single: 0, Double: 0, Twin: 0, ROH: 0, Suite: 0, Staff: 0, VIP: 0 };
}

/** `YYYY-MM-DD` — used to validate date keys at the JSONB boundary. */
const DATE_KEY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Coerce a value to a non-negative integer; non-numbers and negatives → 0.
 */
function toCount(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return 0;
  return Math.floor(value);
}

/**
 * Build a fresh `RoomCounts` from a partial source — missing keys become 0.
 */
function pickCounts(source: Record<string, unknown>): RoomCounts {
  return {
    Single: toCount(source.Single),
    Double: toCount(source.Double),
    Twin: toCount(source.Twin),
    ROH: toCount(source.ROH),
    Suite: toCount(source.Suite),
    Staff: toCount(source.Staff),
    VIP: toCount(source.VIP),
  };
}

/**
 * Normalize the raw `accommodations` JSONB column to the canonical
 * `RoomsByNight` map. Accepts the three shapes seen at runtime: legacy
 * object `{ [date]: counts }`, post-`move-draft-to-real` array
 * `[{ date, ...counts }]`, or null/garbage. Always returns a fresh object
 * with all seven `RoomType` keys present per night (zero-filled).
 *
 * @param raw - Untrusted JSONB value from the events row.
 * @returns Canonical map keyed by `YYYY-MM-DD`.
 */
export function normalizeAccommodations(raw: unknown): RoomsByNight {
  if (!raw || typeof raw !== "object") return {};

  if (Array.isArray(raw)) {
    const out: RoomsByNight = {};
    for (const item of raw) {
      if (!item || typeof item !== "object") continue;
      const { date, ...rest } = item as Record<string, unknown>;
      if (typeof date !== "string" || !DATE_KEY_REGEX.test(date)) continue;
      out[date] = pickCounts(rest);
    }
    return out;
  }

  const out: RoomsByNight = {};
  for (const [date, counts] of Object.entries(raw as Record<string, unknown>)) {
    if (!DATE_KEY_REGEX.test(date) || !counts || typeof counts !== "object") continue;
    out[date] = pickCounts(counts as Record<string, unknown>);
  }
  return out;
}

/**
 * Serialize a canonical `RoomsByNight` map to the array shape persisted on
 * `nowadays.events.accommodations` (matches `move-draft-to-real`'s output
 * and what `EventScheduleSection` reads). Sorted by date for stable diffs.
 *
 * @param map - Canonical in-memory accommodations.
 * @returns Array of nightly entries with date + counts.
 */
export function serializeAccommodationsForSave(map: RoomsByNight): AccommodationsRow {
  return Object.keys(map)
    .sort()
    .map((date) => ({ date, ...createZeroCounts(), ...map[date] }));
}

/**
 * Coerce raw `events.accommodations` (array shape OR legacy date-keyed object —
 * both exist in prod) into the canonical sorted array shape for rendering.
 * Use this in any reader that maps over accommodations so object-shaped events
 * don't silently render as empty.
 *
 * @param raw - The raw `events.accommodations` jsonb value (array or date-keyed
 *   object; null/garbage and any non-object such as a string normalize to empty).
 * @returns Sorted array of nightly entries with all room-type counts present.
 */
export function normalizeAccommodationsToArray(raw: unknown): AccommodationsRow {
  return serializeAccommodationsForSave(normalizeAccommodations(raw));
}

/**
 * Get the peak (maximum) room count from accommodations data
 * @param accommodations - The accommodations array or object
 * @returns The maximum number of rooms needed on any single day
 */
export function getPeakRoomCount(accommodations: any): number {
  if (!accommodations || typeof accommodations !== "object") {
    return 0;
  }

  // Handle array format
  if (Array.isArray(accommodations)) {
    let peakRooms = 0;
    accommodations.forEach((accommodation: any) => {
      const totalForDay = Object.entries(accommodation)
        .filter(([key]) => key !== "date")
        .reduce((sum: number, [_, count]) => {
          return sum + (typeof count === "number" ? count : 0);
        }, 0);
      if (totalForDay > peakRooms) {
        peakRooms = totalForDay;
      }
    });
    return peakRooms;
  }

  // Handle object format
  let peakRooms = 0;
  Object.values(accommodations).forEach((dayAccommodations: any) => {
    if (dayAccommodations && typeof dayAccommodations === "object") {
      const totalForDay = Object.values(dayAccommodations).reduce((sum: number, count: any) => {
        return sum + (typeof count === "number" ? count : 0);
      }, 0);
      if (totalForDay > peakRooms) {
        peakRooms = totalForDay;
      }
    }
  });

  return peakRooms;
}

/**
 * Format accommodations for display in Slack notifications
 * Uses the same logic as EventDetailsSidePanel.tsx and EventDetailsPopup.tsx
 * @param accommodations - The accommodations array or object
 * @returns Formatted string with peak count and daily breakdown
 */
export function formatAccommodationsForSlack(accommodations: any): string {
  if (!accommodations || typeof accommodations !== "object") {
    return "Not specified";
  }

  // Convert to array format if needed (same as EventDetailsPopup)
  const accommodationsArray = Array.isArray(accommodations)
    ? accommodations
    : Object.entries(accommodations).map(([date, rooms]) => ({ date, ...(rooms as any) }));

  if (accommodationsArray.length === 0) {
    return "Not specified";
  }

  // Calculate peak rooms using the array format
  const peakRooms = getPeakRoomCount(accommodationsArray);
  const roomBreakdown: string[] = [];

  accommodationsArray.forEach((accommodation) => {
    const roomTypes = Object.entries(accommodation)
      .filter(([key, value]) => key !== "date" && typeof value === "number" && value > 0)
      .map(([type, count]) => {
        const formattedType = type.replace(/([A-Z])/g, " $1").trim();
        return `${count} ${formattedType}`;
      })
      .join(", ");

    if (roomTypes && accommodation.date) {
      // Use moment like EventDetailsSidePanel and EventDetailsPopup
      const dateStr = moment(accommodation.date).format("MMM D, YYYY");
      roomBreakdown.push(`${dateStr}: ${roomTypes}`);
    }
  });

  if (roomBreakdown.length === 0) {
    return "Not specified";
  }

  return `Peak: ${peakRooms} rooms\nBreakdown:\n${roomBreakdown.join("\n")}`;
}

/**
 * Format accommodations for RFP emails with HTML formatting
 * @param accommodations - The accommodations JSON array or object
 * @returns HTML formatted string or null if no accommodations
 */
export function formatAccommodationsForRFP(accommodations: any): string | null {
  const normalized = normalizeAccommodations(accommodations);
  const dates = Object.keys(normalized).sort();
  if (dates.length === 0) return null;

  const peakRooms = getPeakRoomCount(accommodations);
  if (peakRooms === 0) return null;

  const breakdownLines: string[] = [];
  for (const date of dates) {
    const counts = normalized[date];
    const roomTypes = ROOM_TYPES.filter((type) => counts[type] > 0)
      .map((type) => `${counts[type]} ${type}`)
      .join(", ");
    if (roomTypes) {
      const dateStr = moment(date).format("MMM D, YYYY");
      breakdownLines.push(`${dateStr}: ${roomTypes}`);
    }
  }

  if (breakdownLines.length === 1) {
    return `${peakRooms} rooms`;
  } else if (breakdownLines.length > 1) {
    return `${peakRooms} rooms (peak)<br>Breakdown by date:<br>${breakdownLines.join("<br>")}`;
  }
  return `${peakRooms} rooms`;
}
